<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { api } from '@/api/client';
import type { ConnectionEvent, PortalSite } from '@/api/types';
import SiteStatusBadge from '@/components/SiteStatusBadge.vue';
import TimeAgo from '@/components/TimeAgo.vue';
import NotificationsPanel from '@/components/NotificationsPanel.vue';
import { useUserStore } from '@/stores/user';
import { openRdp } from '@/lib/rdp';

const HISTORY_COLLAPSED_LIMIT = 10;

const props = defineProps<{ siteId: string }>();
const site = ref<PortalSite | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);
const history = ref<ConnectionEvent[]>([]);
const historyLoading = ref(true);
const historyError = ref<string | null>(null);
const historyExpanded = ref(false);

const userStore = useUserStore();
const isInternal = computed(() => userStore.identity?.isInternal ?? false);
const extended = computed(() => (isInternal.value ? site.value?.extended ?? null : null));

// Country code → flag emoji (regional indicator pair). Falls back to the
// raw code if it isn't a 2-letter ISO-3166-1 alpha-2.
function flag(code?: string): string {
    if (!code || code.length !== 2) return '';
    const cc = code.toUpperCase();
    return String.fromCodePoint(
        0x1f1e6 + (cc.charCodeAt(0) - 65),
        0x1f1e6 + (cc.charCodeAt(1) - 65),
    );
}

const regionLabel = computed(() => {
    const e = extended.value;
    if (!e?.countryCode) return null;
    let label = '';
    try {
        const dn = new Intl.DisplayNames(['en'], { type: 'region' });
        label = dn.of(e.countryCode.toUpperCase()) ?? e.countryCode;
    } catch {
        label = e.countryCode;
    }
    if (e.cityName) label = `${e.cityName}, ${label}`;
    return label;
});

const osLabel = computed(() => {
    const e = extended.value;
    if (!e?.os) return null;
    return e.kernelVersion ? `${e.os} ${e.kernelVersion}` : e.os;
});

const ipCopied = ref(false);
async function copyIp(ip: string) {
    if (!ip) return;
    try {
        await navigator.clipboard.writeText(ip);
        ipCopied.value = true;
        setTimeout(() => { ipCopied.value = false; }, 1500);
    } catch {
        // Clipboard unavailable (e.g. insecure context) — the IP is still on screen.
    }
}

const visibleHistory = computed(() =>
    historyExpanded.value ? history.value : history.value.slice(0, HISTORY_COLLAPSED_LIMIT),
);
const hiddenCount = computed(() =>
    Math.max(0, history.value.length - HISTORY_COLLAPSED_LIMIT),
);

onMounted(async () => {
    const siteUrl = `/api/sites/${encodeURIComponent(props.siteId)}`;
    try {
        site.value = await api<PortalSite>(siteUrl);
    } catch (e) { error.value = (e as Error).message; }
    finally { loading.value = false; }

    // Connection history is best-effort: a missing Postgres or an empty table
    // shouldn't blank out the rest of the page. Surface the error inline.
    try {
        history.value = await api<ConnectionEvent[]>(`${siteUrl}/connection-history`);
    } catch (e) { historyError.value = (e as Error).message; }
    finally { historyLoading.value = false; }
});
</script>

<template>
    <div class="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <RouterLink to="/sites" class="text-bp-link hover:text-bp-link-hover text-sm">← back</RouterLink>
        <div v-if="loading" class="mt-4 text-slate-400">Loading…</div>
        <div v-else-if="error" class="mt-4 bg-red-900/40 border border-red-700 px-3 py-2 rounded">{{ error }}</div>
        <div v-else-if="site" class="mt-4 space-y-6">
            <div class="flex items-baseline gap-3 flex-wrap">
                <h1 class="text-2xl font-semibold break-all">{{ site.name }}</h1>
                <SiteStatusBadge :online="site.online" />
            </div>
            <div class="text-slate-400 text-xs sm:text-sm break-all">{{ site.siteId }}</div>

            <section class="bg-bp-panel border border-bp-border rounded p-3 sm:p-4 text-sm">
                <h2 class="text-sm font-semibold uppercase text-slate-400 mb-3 tracking-wide">NetBird peer</h2>
                <!-- Single column on phones: a two-column grid forces the term
                     and value into very narrow boxes and the value-side wraps
                     unpredictably. From sm: upwards the grid pairs them up. -->
                <dl class="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
                    <dt class="text-slate-400">Status</dt>
                    <dd>{{ site.online ? 'Online' : 'Offline' }}</dd>
                    <dt class="text-slate-400">NetBird IP</dt>
                    <dd class="font-mono break-all">{{ site.ip || '—' }}</dd>
                    <dt class="text-slate-400">{{ site.online ? 'Connected since' : 'Last seen' }}</dt>
                    <dd><TimeAgo :iso="site.lastSeen" /></dd>
                </dl>
                <dl
                    v-if="extended"
                    class="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-4 gap-y-2 mt-4 pt-4 border-t border-bp-border"
                >
                    <dt class="text-slate-400">Public IP</dt>
                    <dd class="font-mono break-all">{{ extended.publicIp || '—' }}</dd>
                    <dt class="text-slate-400">Domain name</dt>
                    <dd class="font-mono break-all">{{ extended.domainName || '—' }}</dd>
                    <dt class="text-slate-400">Hostname</dt>
                    <dd class="font-mono break-all">{{ extended.hostname || '—' }}</dd>
                    <dt class="text-slate-400">Region</dt>
                    <dd>
                        <template v-if="regionLabel">
                            <span class="mr-1">{{ flag(extended.countryCode) }}</span>{{ regionLabel }}
                        </template>
                        <template v-else>—</template>
                    </dd>
                    <dt class="text-slate-400">Operating system</dt>
                    <dd class="break-words">{{ osLabel || '—' }}</dd>
                    <dt class="text-slate-400">Serial number</dt>
                    <dd class="font-mono break-all">{{ extended.serialNumber || '—' }}</dd>
                    <dt class="text-slate-400">Registered on</dt>
                    <dd>
                        <TimeAgo v-if="extended.lastLogin" :iso="extended.lastLogin" verbose />
                        <template v-else>—</template>
                    </dd>
                    <dt class="text-slate-400">Agent version</dt>
                    <dd class="font-mono break-all">{{ extended.agentVersion || '—' }}</dd>
                </dl>
            </section>

            <section class="bg-bp-panel border border-bp-border rounded p-3 sm:p-4 text-sm">
                <h2 class="text-sm font-semibold uppercase text-slate-400 mb-3 tracking-wide">Quick connect</h2>
                <p v-if="!site.ip" class="text-slate-500">
                    No NetBird IP yet — site is not enrolled.
                </p>
                <div v-else class="space-y-3">
                    <div class="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            class="bg-bp-accent hover:bg-bp-accent-hover text-white text-xs px-3 py-1.5 rounded transition-colors"
                            @click="openRdp(site.ip, site.name)"
                        >Open RDP</button>
                        <a
                            :href="`https://${site.ip}/admin`"
                            target="_blank"
                            rel="noopener"
                            class="bg-bp-accent hover:bg-bp-accent-hover text-white text-xs px-3 py-1.5 rounded transition-colors"
                        >Open HTTPS</a>
                        <button
                            type="button"
                            class="text-bp-link hover:text-bp-link-hover text-xs underline px-1 py-1.5"
                            @click="copyIp(site.ip)"
                        >{{ ipCopied ? 'Copied' : 'Copy IP' }}</button>
                    </div>
                    <p class="text-xs text-slate-500">
                        "Open RDP" downloads a small <code>.rdp</code> file — open it to start
                        the session in Remote Desktop / mstsc. NetBird must be connected on your
                        machine, and only protocols the site actually exposes will respond.
                    </p>
                </div>
            </section>

            <NotificationsPanel :site-id="site.siteId" />

            <section class="bg-bp-panel border border-bp-border rounded p-3 sm:p-4 text-sm">
                <h2 class="text-sm font-semibold uppercase text-slate-400 mb-3 tracking-wide">Connection history</h2>
                <div v-if="historyLoading" class="text-slate-400">Loading…</div>
                <div v-else-if="historyError" class="bg-red-900/40 border border-red-700 px-3 py-2 rounded">
                    {{ historyError }}
                </div>
                <p v-else-if="history.length === 0" class="text-slate-500">
                    No connection events recorded yet. Events appear here as we observe the site
                    going online or offline (checked every couple of minutes).
                </p>
                <ul v-else class="space-y-1">
                    <li
                        v-for="(ev, idx) in visibleHistory"
                        :key="`${ev.observedAt}-${idx}`"
                        class="flex items-center gap-3"
                    >
                        <span
                            class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium w-28"
                            :class="ev.eventType === 'connect'
                                ? 'bg-emerald-900/40 text-emerald-300'
                                : 'bg-slate-800 text-slate-400'"
                        >
                            <span
                                class="w-1.5 h-1.5 rounded-full"
                                :class="ev.eventType === 'connect' ? 'bg-emerald-400' : 'bg-slate-500'"
                            ></span>
                            {{ ev.eventType === 'connect' ? 'Connected' : 'Disconnected' }}
                        </span>
                        <TimeAgo :iso="ev.observedAt" verbose />
                    </li>
                </ul>
                <button
                    v-if="!historyExpanded && hiddenCount > 0"
                    type="button"
                    class="mt-3 text-bp-accent text-xs underline"
                    @click="historyExpanded = true"
                >Show {{ hiddenCount }} more</button>
                <button
                    v-else-if="historyExpanded && history.length > HISTORY_COLLAPSED_LIMIT"
                    type="button"
                    class="mt-3 text-bp-accent text-xs underline"
                    @click="historyExpanded = false"
                >Show less</button>
            </section>
        </div>
    </div>
</template>
